import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, Loading, ErrorBox, PhaseBadge } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useI18n, ROLES } from "@/lib/i18n";
import Overview from "@/components/audit/Overview";
import Team from "@/components/audit/Team";
import Workforce from "@/components/audit/Workforce";
import Sampling from "@/components/audit/Sampling";
import Interviews from "@/components/audit/Interviews";
import Analytics from "@/components/audit/Analytics";
import Evidence from "@/components/audit/Evidence";
import Findings from "@/components/audit/Findings";
import Documentary from "@/components/audit/Documentary";
import Actions from "@/components/audit/Actions";
import Report from "@/components/audit/Report";
import { QuestionnaireLibrary } from "@/pages/Questionnaires";

export default function AuditWorkspace() {
  const { id } = useParams();
  const { tx, lbl } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [audit, setAudit] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => { api.get(`/audits/${id}`).then((r) => setAudit(r.data)).catch((e) => setErr(errMsg(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <ErrorBox error={err === "Not found" ? tx("Audit introuvable ou accès non autorisé", "Audit not found or access not authorised") : err} />;
  if (!audit) return <Loading />;
  const r = audit.my_role;
  const manage = ["admin", "lead"].includes(r);
  const tabs = [
    ["overview", tx("Vue d'ensemble", "Overview"), true],
    ["interviews", tx("Entretiens", "Interviews"), true],
    ["questionnaires", tx("Questionnaires", "Questionnaires"), true],
    ["team", tx("Équipe & invitations", "Team & invitations"), manage || r === "reviewer"],
    ["workforce", tx("Effectifs & auditeurs", "Workforce & auditors"), true],
    ["sampling", tx("Échantillonnage", "Sampling"), manage || r === "reviewer"],
    ["analytics", tx("Analyses", "Analytics"), audit.my_permissions.can_view_analytics],
    ["evidence", tx("Preuves", "Evidence"), true],
    ["documentary", tx("Contrôles documentaires", "Documentary checks"), true],
    ["findings", tx("Constats & risques", "Findings & risks"), true],
    ["actions", tx("Plan d'action & Gantt", "Action plan & Gantt"), true],
    ["report", tx("Rapport", "Report"), true],
  ].filter((t) => t[2]);
  return (
    <div>
      <PageHeader eyebrow={<Link to={`/clients/${audit.organization?.id}`} className="hover:underline">{audit.organization?.name}</Link>} title={audit.title}
        subtitle={<span className="flex flex-wrap items-center gap-2"><PhaseBadge phase={audit.phase} /><span>{audit.period}</span><span>·</span><span>{tx("Mon rôle", "My role")}: <b data-testid="my-audit-role">{lbl(ROLES, r)}</b></span></span>} />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="mb-4 flex-wrap h-auto bg-[#EAF3FA]">
          {tabs.map(([k, label]) => <TabsTrigger key={k} value={k} data-testid={`audit-tab-${k}`} className="data-[state=active]:bg-white data-[state=active]:text-[#153A63]">{label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><Overview audit={audit} onSaved={load} /></TabsContent>
        <TabsContent value="interviews"><Interviews audit={audit} /></TabsContent>
        <TabsContent value="questionnaires"><QuestionnaireLibrary audit={audit} /></TabsContent>
        <TabsContent value="team"><Team audit={audit} /></TabsContent>
        <TabsContent value="workforce"><Workforce audit={audit} /></TabsContent>
        <TabsContent value="sampling"><Sampling audit={audit} /></TabsContent>
        <TabsContent value="analytics"><Analytics audit={audit} /></TabsContent>
        <TabsContent value="evidence"><Evidence audit={audit} /></TabsContent>
        <TabsContent value="findings"><Findings audit={audit} /></TabsContent>
        <TabsContent value="documentary"><Documentary audit={audit} /></TabsContent>
        <TabsContent value="actions"><Actions audit={audit} /></TabsContent>
        <TabsContent value="report"><Report audit={audit} /></TabsContent>
      </Tabs>
    </div>
  );
}
